import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { classList } from "../../utils";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import { fetchFeeMasterDetails } from "../../redux/feeMasterSlice";
import { useDispatch, useSelector } from "react-redux";

const SENIOR_SECONDARY_CLASSES = ["XI", "XII"];

const isSeniorSecondaryClass = (cls) => {
  if (!cls) return false;
  return SENIOR_SECONDARY_CLASSES.includes(cls.toString().trim().toUpperCase());
};

const STREAM_OPTIONS = ["Science", "Commerce", "Humanities"].map((stream) => ({
  label: stream,
  value: stream,
}));

function FeesMaster() {
  const [formData, setFormData] = useState({
    class: "",
    stream: "",
    admissionFee: "",
    monthlyFee: "",
    developmentCharges: "",
    labFee: "",
  });

  const showStreamFields = isSeniorSecondaryClass(formData.class);

  useEffect(() => {
    if (!showStreamFields && (formData.stream || formData.labFee)) {
      setFormData((prev) => ({ ...prev, stream: "", labFee: "" }));
    }
  }, [formData.class]);

  const dispatch = useDispatch();
  const { data: feeMasterDetails, loading: fetchLoading } = useSelector(
    (state) => state.feeMaster,
  );

  useEffect(() => {
    dispatch(fetchFeeMasterDetails());
  }, [dispatch]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: false }));
  };

  const resetForm = () => {
    setFormData({
      class: "",
      stream: "",
      admissionFee: "",
      monthlyFee: "",
      developmentCharges: "",
      labFee: "",
    });
    setIsEditing(false);
    setEditId(null);
  };

  const handleEdit = (feeHead) => {
    setFormData({
      class: feeHead.class,
      stream: feeHead.stream || "",
      admissionFee: feeHead.admissionFee,
      monthlyFee: feeHead.monthlyFee,
      developmentCharges: feeHead.developmentCharges,
      labFee: feeHead.labFee || "",
    });

    setEditId(feeHead._id);
    setIsEditing(true);

    const modal = new window.bootstrap.Modal(document.querySelector(".modal"));
    modal.show();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const seniorOnlyFields = ["stream", "labFee"];

    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (seniorOnlyFields.includes(key) && !showStreamFields) return;
      if (typeof formData[key] === "string" && !formData[key].trim()) {
        newErrors[key] = true;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setError(newErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        class: formData.class || "",
      };

      const url = isEditing
        ? `${server}/update_feeHead_mongodb`
        : `${server}/addFeeHeadMongoDb`;

      await axios.post(url, payload, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(isEditing ? "Fee Head Updated" : "Fee Head Added", {
        position: "top-right",
      });

      fetchFeeMasterDetails();
      document.querySelector('[data-bs-dismiss="modal"]')?.click();
      resetForm();
      setIsEditing(false);
      setEditId(null);
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const filteredRecords = feeMasterDetails.filter((feesMaster) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(feesMaster || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleExportFeeMasterExcel = () => {
    if (!feeMasterDetails || feeMasterDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = feeMasterDetails.map((record) => ({
      Class: record?.class || record?.feesMaster?.class || "",
      Stream: record?.stream || record?.feesMaster?.stream || "",
      "Admission Fee":
        record?.admissionFee || record?.feesMaster?.admissionFee || "",
      "Monthly Fee": record?.monthlyFee || record?.feesMaster?.monthlyFee || "",
      "Development Charges":
        record?.developmentCharges ||
        record?.feesMaster?.developmentCharges ||
        "",
      "Lab Fee": record?.labFee || record?.feesMaster?.labFee || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Master");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `FeeMaster_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportMonthlyFeeExcel = () => {
    if (!feeMasterDetails || feeMasterDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = feeMasterDetails.map((record) => ({
      Class: record?.class || record?.feesMaster?.class || "",
      "Monthly Fee": record?.monthlyFee || record?.feesMaster?.monthlyFee || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Fee");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `MonthlyFee_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportDevelopmentChargesExcel = () => {
    if (!feeMasterDetails || feeMasterDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = feeMasterDetails.map((record) => ({
      Class: record?.class || record?.feesMaster?.class || "",
      "Development Charges":
        record?.developmentCharges ||
        record?.feesMaster?.developmentCharges ||
        "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Development Charges");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `DevelopmentCharges_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportLabFeeExcel = () => {
    if (!feeMasterDetails || feeMasterDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = feeMasterDetails
      .filter((record) => record?.labFee || record?.feesMaster?.labFee)
      .map((record) => ({
        Class: record?.class || record?.feesMaster?.class || "",
        Stream: record?.stream || record?.feesMaster?.stream || "",
        "Lab Fee": record?.labFee || record?.feesMaster?.labFee || "",
      }));

    if (sheetData.length === 0) {
      toast.info("No Lab Fee records found");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Lab Fee");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `LabFee_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAdmissionFeeExcel = () => {
    if (!feeMasterDetails || feeMasterDetails.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = feeMasterDetails.map((record) => ({
      Class: record?.class || record?.feesMaster?.class || "",
      "Admission Fee":
        record?.admissionFee || record?.feesMaster?.admissionFee || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admission Fee");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AdmissionFee_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div
              class="modal fade gd-example-modal-lg"
              tabindex="-1"
              role="dialog"
              aria-labelledby="myLargeModalLabel"
              aria-hidden="true"
            >
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <form onSubmit={handleSubmit}>
                    <div class="modal-header d-flex justify-content-between">
                      <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                        Add Fee Head{" "}
                        <span className="ms-3 text-secondary">(01/04)</span>
                      </h5>
                      <div className="d-flex align-items-center">
                        <span class="badge bg-success-soft me-3 px-3">
                          Editable
                        </span>
                        <div class="form-check form-switch me-4">
                          <input
                            type="checkbox"
                            class="form-check-input m-0"
                            checked
                          />
                        </div>
                        <button
                          type="button"
                          class="btn-close"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>

                    <div class="modal-body">
                      <div className="row g-2 mb-2">
                        <div className="col-lg-6 col-12">
                          <label class="form-label mb-1 ms-3">
                            Class <span className="text-danger">*</span>
                          </label>
                          <Select
                            name="class"
                            value={
                              classList
                                .map((cls) => ({
                                  label: cls,
                                  value: cls,
                                }))
                                .find((opt) => opt.value === formData.class) ||
                              null
                            }
                            onChange={(selected) =>
                              handleChange({
                                target: {
                                  name: "class",
                                  value: selected?.value || "",
                                },
                              })
                            }
                            options={classList.map((cls) => ({
                              label: cls,
                              value: cls,
                            }))}
                            placeholder="Select Class"
                          />
                        </div>

                        {showStreamFields && (
                          <div className="col-lg-6 col-12">
                            <label class="form-label mb-1 ms-3">
                              Stream <span className="text-danger">*</span>
                            </label>
                            <Select
                              name="stream"
                              value={
                                STREAM_OPTIONS.find(
                                  (opt) => opt.value === formData.stream,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleChange({
                                  target: {
                                    name: "stream",
                                    value: selected?.value || "",
                                  },
                                })
                              }
                              options={STREAM_OPTIONS}
                              placeholder="Select Stream"
                              classNamePrefix="react-select"
                              className={error.stream ? "border-danger" : ""}
                            />
                          </div>
                        )}

                        <div className="col-lg-6 col-12">
                          <label class="form-label mb-1 ms-3">
                            Admission Fee <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control text-dark ${
                              error.admissionFee ? "border-danger" : ""
                            }`}
                            placeholder="Enter Admission Fee"
                            name="admissionFee"
                            value={formData.admissionFee}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="col-lg-6 col-12">
                          <label class="form-label mb-1 ms-3">
                            Monthly Fee <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control text-dark ${
                              error.monthlyFee ? "border-danger" : ""
                            }`}
                            name="monthlyFee"
                            placeholder="Enter Monthly Fee"
                            value={formData.monthlyFee}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="col-lg-6 col-12">
                          <label class="form-label mb-1 ms-3">
                            Development Charges{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control text-dark ${
                              error.developmentCharges ? "border-danger" : ""
                            }`}
                            name="developmentCharges"
                            placeholder="Enter Development Charges"
                            value={formData.developmentCharges}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        {showStreamFields && (
                          <div className="col-lg-6 col-12">
                            <label class="form-label mb-1 ms-3">
                              Lab Fee <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control text-dark ${
                                error.labFee ? "border-danger" : ""
                              }`}
                              name="labFee"
                              placeholder="Enter Lab Fee"
                              value={formData.labFee}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div class="modal-footer d-flex justify-content-between py-2">
                      <div>
                        <button
                          type="button"
                          class="btn btn-secondary px-3 me-1 rounded-pill"
                          data-bs-dismiss="modal"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                        <button
                          type="submit"
                          class="btn btn-primary px-5 rounded-pill fw-normal"
                          disabled={loading}
                        >
                          {loading ? (
                            <div
                              class="spinner-border spinner-border-sm"
                              role="status"
                            ></div>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="">
              <div class="container-fluid my-3 px-3">
                <div className="row g-2">
                  <div className="col-lg-8">
                    <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                      <table class="table mb-0 text-nowrap table-centered">
                        <thead class="table-light">
                          <tr>
                            <th className="w-10">No.</th>
                            <th className="lh-1 w-60">
                              Details
                              <i className="bi bi-info-circle ms-2 text-dark"></i>
                              <br />
                              <span className="fs-8 fw-normal text-secondary">
                                Displaying Information as per Record
                              </span>
                            </th>
                            <th className="lh-1 w-30">
                              Action
                              <i className="bi bi-person-gear ms-2 text-dark"></i>
                              <br />
                              <span className="fs-8 fw-normal text-secondary">
                                Role-Specific Action Panel
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentRecords.map((feesMaster, index) => (
                            <tr key={index}>
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span class="text-dark">Class</span> &mdash;{" "}
                                <span className="text-capitalize">
                                  {feesMaster.class}
                                </span>
                                {feesMaster.stream && (
                                  <span className="badge bg-info-soft ms-3">
                                    {feesMaster.stream}
                                  </span>
                                )}
                                <hr className="w-25" />
                                <span class="text-dark">
                                  Admission Fee
                                </span>{" "}
                                &mdash; {feesMaster.admissionFee}
                                <br />
                                <span class="text-dark">Monthly Fee</span>{" "}
                                &mdash; {feesMaster.monthlyFee}
                                <br />
                                <span class="text-dark">
                                  Development Charges
                                </span>{" "}
                                &mdash; {feesMaster.developmentCharges}
                                {feesMaster.labFee && (
                                  <>
                                    <br />
                                    <span class="text-dark">Lab Fee</span>{" "}
                                    &mdash; {feesMaster.labFee}
                                  </>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <span
                                    onClick={() => handleEdit(feesMaster)}
                                    class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Update
                                  </span>
                                  <span class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                    Delete
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div class="">
                      <div className="mb-3">
                        <div className="d-flex bg-white justify-content-between rounded-4">
                          <form
                            className="d-flex align-items-center"
                            onSubmit={(e) => e.preventDefault()}
                          >
                            <span className="position-absolute ps-3 search-icon">
                              <i className="bi bi-filter-circle-fill ms-1" />
                            </span>
                            <input
                              type="search"
                              className="form-control ps-7 rounded-3 border-0 rounded-4"
                              placeholder="Filter Data"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </form>
                          <Link
                            data-bs-toggle="modal"
                            onClick={resetForm}
                            data-bs-target=".gd-example-modal-lg"
                            className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                          >
                            Add Fee Head
                          </Link>
                        </div>
                        <div className="mt-2 ms-4">
                          <nav aria-label="breadcrumb">
                            <ol class="breadcrumb pe-3">
                              <li class="breadcrumb-item">
                                <a href="#">Fees Collection</a>
                              </li>
                              <li
                                class="breadcrumb-item active"
                                aria-current="page"
                              >
                                Monthly Fees
                              </li>
                            </ol>
                          </nav>
                        </div>
                      </div>

                      <div className="p-4 bg-white rounded-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            {fetchLoading ? (
                              <div
                                className="spinner-border text-dark data-loader-spinner"
                                role="status"
                              ></div>
                            ) : (
                              <img src="/assets/images/folder.svg" />
                            )}

                            <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                              Fees Master
                              <br />
                              <span className="text-secondary fs-6 fw-medium lh-sm">
                                0.6 MB Size
                              </span>
                            </p>
                          </div>
                          <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                            Export
                          </span>
                        </div>

                        <div class="px-4 py-2 mt-3 bg-light rounded-4">
                          <li className="list-group-item px-0">
                            <a
                              className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                              data-bs-toggle="collapse"
                              href="#specifications"
                              role="button"
                              aria-expanded="false"
                              aria-controls="specifications"
                            >
                              <p className="m-0 fw-medium lh-sm text-dark">
                                Download Report
                              </p>
                              <span className="chevron-arrow ms-4">
                                <i className="bi bi-chevron-down fs-4" />
                              </span>
                            </a>
                            <div
                              className="collapse"
                              id="specifications"
                              data-bs-parent="#ecommerceAccordion"
                            >
                              <div className="py-3">
                                <div class="rounded-4 d-flex flex-wrap">
                                  <span
                                    onClick={handleExportFeeMasterExcel}
                                    class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  >
                                    All Fees Report
                                  </span>
                                  <span
                                    onClick={handleExportMonthlyFeeExcel}
                                    class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  >
                                    Monthly Fee Report
                                  </span>
                                  <span
                                    onClick={
                                      handleExportDevelopmentChargesExcel
                                    }
                                    class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  >
                                    Development Charges Report
                                  </span>
                                  <span
                                    onClick={handleExportAdmissionFeeExcel}
                                    class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  >
                                    Admission Fee Report
                                  </span>
                                  <span
                                    onClick={handleExportLabFeeExcel}
                                    class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                  >
                                    Lab Fee Report
                                  </span>
                                </div>
                              </div>
                            </div>
                          </li>
                        </div>
                      </div>

                      <div className="my-4">
                        <nav>
                          <ul className="pagination justify-content-center mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button className="page-link" onClick={prevPage}>
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>

                            {(() => {
                              const pageNumbers = [];
                              pageNumbers.push(1);

                              if (currentPage > 3) {
                                pageNumbers.push("left-ellipsis");
                              }

                              if (
                                currentPage !== 1 &&
                                currentPage !== totalPages
                              ) {
                                pageNumbers.push(currentPage);
                              }

                              if (currentPage < totalPages - 2) {
                                pageNumbers.push("right-ellipsis");
                              }

                              if (totalPages > 1) {
                                pageNumbers.push(totalPages);
                              }

                              return pageNumbers.map((page, index) => {
                                if (
                                  page === "left-ellipsis" ||
                                  page === "right-ellipsis"
                                ) {
                                  return (
                                    <li
                                      key={index}
                                      className="page-item disabled"
                                    >
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }

                                return (
                                  <li
                                    key={index}
                                    className={`page-item ${
                                      currentPage === page ? "active" : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              });
                            })()}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button className="page-link" onClick={nextPage}>
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default FeesMaster;
